import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Printer, RotateCcw, Save } from 'lucide-react';
import { printSample, qzErrorMessage } from '../lib/qzPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOGGLES = [
  ['show_branch_contact', 'Branch address & contact'], ['show_supplier', 'Supplier'], ['show_branch', 'Branch'], ['show_date', 'Date'],
  ['show_order_name', 'PO number'], ['show_confirmed_by', 'Confirmed by'], ['show_unit', 'Count unit after item'], ['show_notes', 'Notes'],
  ['show_item_count', 'Item count'], ['large_items', 'Large item text'],
];

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

export const DocketLayoutPanel = ({ printers }) => {
  const [layout, setLayout] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [preview, setPreview] = useState('');
  const [printerId, setPrinterId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/print-layout`, { withCredentials: true })
      .then((r) => { setLayout(r.data.layout); setDefaults(r.data.defaults); });
  }, []);

  useEffect(() => {
    if (!printerId && printers.length) setPrinterId((printers.find((p) => p.is_default) || printers[0]).printer_id);
  }, [printers, printerId]);

  useEffect(() => {
    if (!layout) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API}/print/layout-preview`, { layout }, { withCredentials: true })
        .then((r) => setPreview(r.data.preview)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [layout]);

  if (!layout) return null;
  const set = (k, v) => setLayout((l) => ({ ...l, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/print-layout`, { layout }, { withCredentials: true });
      setLayout(r.data.layout);
      toast.success('Docket layout saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save layout'); }
    finally { setSaving(false); }
  };

  const sample = async () => {
    if (!printerId) { toast.error('Add a printer first'); return; }
    try {
      const p = await printSample(layout, printerId);
      toast.success(`Sample sent to ${p.name || p.ip_address}`);
    } catch (e) { toast.error(qzErrorMessage(e)); }
  };

  return (
    <Card data-testid="docket-layout-panel">
      <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Docket Layout</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Header title"><Input value={layout.title} onChange={(e) => set('title', e.target.value)} data-testid="layout-title-input" /></Field>
            <Field label="Subtitle"><Input value={layout.subtitle} onChange={(e) => set('subtitle', e.target.value)} data-testid="layout-subtitle-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {TOGGLES.map(([k, label]) => (
              <label key={k} className="flex items-center justify-between gap-2 text-sm">
                {label}
                <Switch checked={!!layout[k]} onCheckedChange={(v) => set(k, v)} data-testid={`layout-toggle-${k}`} />
              </label>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Characters per line">
              <Select value={String(layout.chars_per_line)} onValueChange={(v) => set('chars_per_line', Number(v))}>
                <SelectTrigger data-testid="layout-chars-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="48">48 (80mm)</SelectItem>
                  <SelectItem value="42">42 (80mm narrow)</SelectItem>
                  <SelectItem value="32">32 (58mm)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Blank lines before cut">
              <Input type="number" min="0" max="20" value={layout.feed_lines}
                onChange={(e) => set('feed_lines', Math.max(0, Math.min(20, parseInt(e.target.value, 10) || 0)))} data-testid="layout-feed-input" />
            </Field>
            <Field label="Cut">
              <Select value={layout.cut} onValueChange={(v) => set('cut', v)}>
                <SelectTrigger data-testid="layout-cut-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full cut</SelectItem>
                  <SelectItem value="partial">Partial cut</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Footer message (one line per row)"><Textarea rows={3} value={layout.footer} onChange={(e) => set('footer', e.target.value)} data-testid="layout-footer-input" /></Field>
          <div className="flex flex-wrap gap-2 items-end">
            <Button onClick={save} disabled={saving} data-testid="layout-save-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save layout'}</Button>
            <Button variant="outline" onClick={() => setLayout(defaults)} data-testid="layout-reset-btn"><RotateCcw className="w-4 h-4 mr-2" />Defaults</Button>
            <Select value={printerId} onValueChange={setPrinterId}>
              <SelectTrigger className="w-44" data-testid="layout-printer-select"><SelectValue placeholder="Printer" /></SelectTrigger>
              <SelectContent>{printers.map((p) => <SelectItem key={p.printer_id} value={p.printer_id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" onClick={sample} data-testid="layout-print-sample-btn"><Printer className="w-4 h-4 mr-2" />Print sample</Button>
          </div>
          <p className="text-xs text-muted-foreground">"Print sample" uses the settings on screen, so you can try them before saving.</p>
        </div>
        <div>
          <Label className="text-xs">Preview (sample order)</Label>
          <div className="mt-1.5 bg-white border rounded-md p-4 overflow-x-auto shadow-sm">
            <pre className="font-mono text-[11px] leading-[1.35] text-black whitespace-pre" data-testid="layout-preview">{preview}</pre>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
